/* eslint-disable office-addins/load-object-before-read */ import TurndownService from "turndown";
import { getOrSetLevel } from "./getOrSetLevel";
import { sectionHeadingLevel } from "./sectionHeading";
import { loadListNumbers, withListNumber } from "./listNumbers";
/**
 * Telltale markers for Word's exported HTML/CSS preamble. When a user pastes
 * Word-exported HTML into a document as text (or another tool injects it),
 * the body ends up with a multi-line HTML comment full of `@font-face`,
 * `MsoNormal`, `WordSection1` etc. The LLM should never see this — it is not
 * document content, it is leaked styling metadata that confuses the model and
 * pollutes the chat-side diff.
 */ const WORD_EXPORT_MARKERS = /\bMso(?:Normal|ChpDefault|\w+Default)\b|\bWordSection\d*\b|@font-face\b|@page\s+\w|\/\*\s*(?:Font|Style|List)\s+Definitions\s*\*\//i;
/**
 * Strip Word-export noise from a plaintext paragraph value. Conservative:
 * only removes content that clearly identifies as Word's HTML export
 * (telltale @-rules / Mso classes), never touches ordinary text.
 *
 * Exported so callers outside this file (selection/document markdown helpers)
 * can apply the same sanitization before sending content to the LLM.
 */ export function stripWordExportNoise(text) {
    if (!text || !WORD_EXPORT_MARKERS.test(text)) return text;
    let result = text;
    // Multi-line HTML comment containing CSS — the canonical case.
    result = result.replace(/<!--[\s\S]*?-->/g, (match)=>WORD_EXPORT_MARKERS.test(match) ? "" : match);
    // Standalone @-rules that survive paragraph splitting.
    result = result.replace(/@(?:font-face|page|media|import|charset|keyframes|supports)\b[^{]*\{[^}]*\}/gi, "");
    // Word-style class rules: `p.MsoNormal, li.MsoNormal { ... }`,
    // `.MsoChpDefault { ... }`, `div.WordSection1 { ... }`.
    result = result.replace(/(?:[a-z]+)?\s*\.Mso\w+(?:\s*,\s*(?:[a-z]+)?\s*\.Mso\w+)*\s*\{[^}]*\}/gi, "");
    result = result.replace(/div\.WordSection\d+\s*\{[^}]*\}/gi, "");
    // Orphan CSS comments left behind ("/* Font Definitions */" etc.)
    result = result.replace(/\/\*\s*(?:Font|Style|List)\s+Definitions\s*\*\//gi, "");
    // If the paragraph still contains export markers after the structured
    // strips above, the content is fragmented across lines (split paste). In
    // that case drop any remaining `<!--` / `-->` bookends and collapse so the
    // residue does not bleed into the LLM context as syntactic gibberish.
    if (WORD_EXPORT_MARKERS.test(result)) {
        result = result.replace(/<!--|-->/g, "");
    }
    return result.replace(/[ \t]+/g, " ").trim();
}
function toMarkdown(text, style) {
    if (style.hidden) return "";
    let result = text;
    // --- basic markdown ---
    if (style.bold) result = `**${result}**`;
    if (style.italic) result = `*${result}*`;
    if (style.strikeThrough) result = `~~${result}~~`;
    if (style.doubleStrikeThrough) result = `~~${result}~~`; // no true double
    // --- extra formatting with HTML ---
    if (style.underline && style.underline !== "None") {
        result = `<u>${result}</u>`;
    }
    if (style.subscript) result = `<sub>${result}</sub>`;
    if (style.superscript) result = `<sup>${result}</sup>`;
    // CSS style wrapper if needed
    if (style.color || style.highlightColor || style.name || style.size) {
        const css = [];
        // if (style.color) css.push(`color:${style.color}`);
        // if (style.highlightColor) css.push(`background-color:${style.highlightColor}`);
        // if (style.name) css.push(`font-family:${style.name}`);
        if (style.size) css.push(`font-size:${style.size}px`);
        result = `<span style="${css.join("; ")}">${result}</span>`;
    }
    // --- header indent ---
    if (style.headerIndent !== undefined && style.headerIndent >= 0) {
        const hashes = "#".repeat(Math.min(style.headerIndent + 1, 6)); // limit to H6
        result = `${hashes} ${result}`;
    }
    return result;
}
function addTrackChangesRules(turndownService) {
    // Handle strikethrough text (deletions)
    turndownService.addRule("strikethrough", {
        filter: [
            "s",
            "del"
        ],
        replacement: function(content) {
            return "~~" + content + "~~ *[deleted]*";
        }
    });
    // Handle underlined text (insertions)
    turndownService.addRule("underline", {
        filter: [
            "u",
            "ins"
        ],
        replacement: function(content) {
            return "**" + content + "** *[inserted]*";
        }
    });
    // Handle span elements with text decoration
    turndownService.addRule("spanWithDecoration", {
        filter: function(node) {
            return node.nodeName === "SPAN" && node.style && (node.style.textDecoration === "line-through" || node.style.textDecoration === "underline");
        },
        replacement: function(content, node) {
            if (node.style.textDecoration === "line-through") {
                return "~~" + content + "~~ *[deleted]*";
            } else if (node.style.textDecoration === "underline") {
                return "**" + content + "** *[inserted]*";
            }
            return content;
        }
    });
    // Handle elements with specific classes that indicate track changes
    turndownService.addRule("trackChangeClasses", {
        filter: function(node) {
            return node.className && (node.className.includes("insertion") || node.className.includes("deletion") || node.className.includes("track-change"));
        },
        replacement: function(content, node) {
            if (node.className.includes("deletion")) {
                return "~~" + content + "~~ *[deleted]*";
            } else if (node.className.includes("insertion")) {
                return "**" + content + "** *[inserted]*";
            } else {
                return content + " *[track change]*";
            }
        }
    });
}
const isParagraphHeader = (paragraph, thresholdLevel)=>{
    const level = sectionHeadingLevel(paragraph, thresholdLevel);
    return level === null ? {
        isHeader: false,
        level
    } : {
        isHeader: true,
        level
    };
};
export const getSectionText = async (onProgress)=>{
    return await Word.run(async (context)=>{
        if (onProgress) onProgress(10);
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items/text, items/uniqueLocalId, items/styleBuiltIn, items/style, items/font, items/tableNestingLevel, items/isListItem");
        await context.sync();
        // Word's list numbers are not in the text, and lawyers refer to clauses by them.
        const listNumbers = await loadListNumbers(context, paragraphs.items);
        if (onProgress) onProgress(30);
        const turndownService = new TurndownService({
            headingStyle: "atx"
        });
        addTrackChangesRules(turndownService);
        if (onProgress) onProgress(50);
        const thresholdLevel = await getOrSetLevel();
        const sections = [];
        let currentHeaderEndIndex = paragraphs.items.length;
        for(let i = paragraphs.items.length - 1; i >= 0; i--){
            const isFirstParagraph = i === 0;
            const para = paragraphs.items[i];
            const { isHeader, level } = isParagraphHeader(para, thresholdLevel);
            if (isHeader || isFirstParagraph) {
                const paragraphsInSection = paragraphs.items.slice(i, currentHeaderEndIndex);
                const markdownParagraphs = paragraphsInSection.map((p, j)=>toMarkdown(withListNumber(stripWordExportNoise(p.text), listNumbers[i + j]), {
                        ...p.font,
                        headerIndent: j === 0 ? level ?? undefined : undefined
                    }));
                // Empty paragraphs, such as the cells of a table pending deletion, read as at most one blank line.
                const sectionText = markdownParagraphs.join("\n").replace(/\n{3,}/g, "\n\n").trim();
                const isSectionEmpty = sectionText.length === 0;
                currentHeaderEndIndex = i;
                if (isSectionEmpty) {
                    continue;
                }
                sections.push({
                    header: withListNumber(stripWordExportNoise(para.text), listNumbers[i]),
                    level: level ?? 1,
                    section: sectionText,
                    hash: para.uniqueLocalId
                });
            }
        }
        if (onProgress) onProgress(100);
        return sections.reverse();
    });
};
export const getParagraphsForIndexing = async (onProgress)=>{
    return await Word.run(async (context)=>{
        if (onProgress) onProgress(10);
        // Load all data in first batch - paragraphs and body tracked changes
        const paragraphsProxys = context.document.body.paragraphs;
        paragraphsProxys.load("items/text, items/uniqueLocalId, items/styleBuiltIn, items/font, items/tableNestingLevel");
        const fullRange = context.document.body.getRange("Whole");
        const bodyTC = fullRange.getTrackedChanges();
        bodyTC.load("items/type");
        // First sync - get basic paragraph and body-level tracked changes data
        await context.sync();
        if (onProgress) onProgress(30);
        const changeLoads = [];
        // Comprehensive tracked change type detection
        const getTrackedChangeKind = (rawType)=>{
            // Handle numeric Word TrackedChangeType enum values
            if (typeof rawType === "number") {
                // Common Word TrackedChangeType enum values
                switch(rawType){
                    case 0:
                        return "added"; // Insertion
                    case 1:
                        return "deleted"; // Deletion
                    case 2:
                        return null; // Format change (ignore)
                    case 3:
                        return null; // Move (ignore)
                    case 4:
                        return null; // Table cell insertion (ignore)
                    case 5:
                        return null; // Table cell deletion (ignore)
                    case 6:
                        return null; // Cell merge (ignore)
                    case 7:
                        return null; // Cell split (ignore)
                }
                // Try accessing Word.TrackedChangeType if available
                const wordGlobal = Word;
                if (wordGlobal.TrackedChangeType) {
                    const E = wordGlobal.TrackedChangeType;
                    if (rawType === E.insertion) return "added";
                    if (rawType === E.deletion) return "deleted";
                }
            }
            // Handle string representations (case-insensitive)
            const ts = String(rawType).toLowerCase();
            if (/\b(insert|insertion|ins|add|added|addition)\b/.test(ts)) return "added";
            if (/\b(delete|deletion|del|remove|removed|removal|deleted)\b/.test(ts)) return "deleted";
            return null;
        };
        for (const ch of bodyTC.items){
            const rawType = ch.type;
            const kind = getTrackedChangeKind(rawType);
            const rng = ch.getRange();
            rng.load("text");
            const paras = rng.paragraphs;
            paras.load("items/uniqueLocalId");
            changeLoads.push({
                kind,
                range: rng,
                paras,
                rawType
            });
        }
        // Second sync - get all tracked change range texts and paragraph IDs
        await context.sync();
        if (onProgress) onProgress(50);
        // Process tracked changes into buckets by paragraph ID
        const changeBucketsById = {};
        const normalizeText = (text)=>{
            const normalized = text.replace(/\r\n|\r|\n/g, " ").replace(/\s+/g, " ").trim();
            return normalized.length > 0 ? normalized : text;
        };
        // Process all tracked changes, including those that might have been missed
        for (const cl of changeLoads){
            const rawText = cl.range.text ?? "";
            const snippet = normalizeText(rawText);
            const ids = cl.paras.items.map((p)=>p.uniqueLocalId);
            // Debug logging for the specific paragraph we're interested in
            const hasTargetParagraph = ids.includes("48E8FDB0-3F73-2046-B113-81D703DFC157");
            if (hasTargetParagraph) {
                console.log("Found tracked change in target paragraph:", {
                    kind: cl.kind,
                    rawType: cl.rawType,
                    rawText,
                    snippet,
                    paragraphIds: ids
                });
            }
            // Process all changes, even those with unknown types
            for (const id of ids){
                const bucket = changeBucketsById[id] ||= {
                    added: [],
                    deleted: [],
                    byType: {}
                };
                if (cl.kind === "added" && snippet.length > 0) {
                    if (!bucket.added.includes(snippet)) {
                        bucket.added.push(snippet);
                        bucket.byType["Added"] = (bucket.byType["Added"] ?? 0) + 1;
                    }
                } else if (cl.kind === "deleted") {
                    // For deletions, even empty snippets might be significant
                    const deleteText = snippet.length > 0 ? snippet : "[deleted content]";
                    if (!bucket.deleted.includes(deleteText)) {
                        bucket.deleted.push(deleteText);
                        bucket.byType["Deleted"] = (bucket.byType["Deleted"] ?? 0) + 1;
                    }
                } else if (cl.kind === null && snippet.length > 0) {
                    // Log unrecognized changes for debugging
                    if (hasTargetParagraph) {
                        console.log("Unrecognized tracked change type in target paragraph:", {
                            rawType: cl.rawType,
                            rawTypeType: typeof cl.rawType,
                            text: snippet,
                            paragraphId: id
                        });
                    }
                }
            }
        }
        // Setup processing utilities
        const turndownService = new TurndownService({
            headingStyle: "atx"
        });
        addTrackChangesRules(turndownService);
        const thresholdLevel = await getOrSetLevel();
        const paragraphs = [];
        let latestSeenHeaderIndentLevel = null;
        const indentPosition = [];
        const maxIndentPosition = 9;
        const getParagraphPosition = (level)=>{
            const normalizedLevel = Math.max(0, Math.min(level, maxIndentPosition));
            indentPosition.length = normalizedLevel + 1;
            indentPosition[normalizedLevel] = (indentPosition[normalizedLevel] ?? -1) + 1;
            for(let i = 0; i < indentPosition.length; i++){
                if (indentPosition[i] === undefined) {
                    indentPosition[i] = 0;
                }
            }
            return [
                ...indentPosition
            ];
        };
        // Process paragraphs using precomputed tracked changes
        for(let i = 0; i < paragraphsProxys.items.length; i++){
            const para = paragraphsProxys.items[i];
            const { isHeader, level } = isParagraphHeader(para, thresholdLevel);
            if (isHeader) {
                latestSeenHeaderIndentLevel = level;
            }
            // Use precomputed tracked changes buckets
            const bucket = changeBucketsById[para.uniqueLocalId];
            const byType = bucket?.byType ?? {};
            const added = bucket?.added ?? [];
            const deleted = bucket?.deleted ?? [];
            const totalTrackedChanges = added.length + deleted.length;
            // Debug log for this specific paragraph
            if (para.uniqueLocalId === "48E8FDB0-3F73-2046-B113-81D703DFC157") {
                console.log("Debug paragraph 48E8FDB0-3F73-2046-B113-81D703DFC157:", {
                    text: para.text,
                    bucket,
                    added,
                    deleted,
                    totalTrackedChanges
                });
            }
            const markdown = toMarkdown(stripWordExportNoise(para.text), {
                ...para.font,
                headerIndent: level ?? undefined
            });
            paragraphs.push({
                position: getParagraphPosition(latestSeenHeaderIndentLevel ?? 0),
                text: markdown,
                hash: para.uniqueLocalId,
                trackedChanges: {
                    total: totalTrackedChanges,
                    byType,
                    added,
                    deleted
                }
            });
        }
        const processedSections = paragraphs.filter((section)=>section.text.trim().length > 0);
        if (onProgress) onProgress(100);
        console.log(processedSections);
        return processedSections;
    });
};
/**
 * Fast lightweight extraction of the document paragraph map.
 * Returns an array of { hash (uniqueLocalId), text, headingLevel } for all paragraphs.
 * Uses a single Word.run with one context.sync - much faster than batch loading.
 * Typically completes in ~100-200ms even for large documents.
 */ export const getDocumentParagraphMap = async ()=>{
    return await Word.run(async (context)=>{
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items/uniqueLocalId,items/text,items/styleBuiltIn,items/tableNestingLevel");
        await context.sync();
        const headingStyles = {
            Heading1: 1,
            Heading2: 2,
            Heading3: 3,
            Heading4: 4,
            Heading5: 5,
            Heading6: 6
        };
        return paragraphs.items.filter((p)=>p.uniqueLocalId && p.text.trim().length > 0).map((p)=>({
                hash: p.uniqueLocalId,
                text: stripWordExportNoise(p.text.trim()),
                headingLevel: p.tableNestingLevel > 0 ? null : headingStyles[p.styleBuiltIn] ?? null
            })).filter((p)=>p.text.length > 0);
    });
};
/**
 * Non-blocking version of getParagraphsForIndexing
 * Uses document sections (by heading) to load paragraphs in true chunks.
 * Each section is loaded in a separate Word.run to prevent Word from freezing.
 *
 * This approach uses getRange to get specific portions of the document.
 */ export const getParagraphsForIndexingAsync = async (onProgress, batchSize = 10)=>{
    const yieldToUI = async (ms = 0)=>{
        await new Promise((resolve)=>setTimeout(resolve, ms));
        await new Promise((resolve)=>requestAnimationFrame(resolve));
    };
    console.log("[getParagraphsForIndexingAsync] Starting chunked ID-first approach...");
    if (onProgress) onProgress(2);
    await yieldToUI(50);
    // STEP 1: Load ONLY the uniqueLocalIds first (minimal data transfer)
    console.log("[getParagraphsForIndexingAsync] Step 1: Loading paragraph IDs only...");
    let paragraphIds = [];
    try {
        paragraphIds = await Word.run(async (context)=>{
            const paragraphs = context.document.body.paragraphs;
            // Load ONLY the uniqueLocalId - absolute minimum payload
            paragraphs.load("items/uniqueLocalId");
            await context.sync();
            console.log("[getParagraphsForIndexingAsync] Found", paragraphs.items.length, "paragraph IDs");
            // Extract just the IDs to plain array
            return paragraphs.items.map((p)=>p.uniqueLocalId || "").filter((id)=>id.length > 0);
        });
    } catch (e) {
        console.error("[getParagraphsForIndexingAsync] Failed to load paragraph IDs:", e);
        return [];
    }
    if (paragraphIds.length === 0) {
        console.log("[getParagraphsForIndexingAsync] No paragraphs found");
        if (onProgress) onProgress(100);
        return [];
    }
    console.log("[getParagraphsForIndexingAsync] Got", paragraphIds.length, "paragraph IDs");
    if (onProgress) onProgress(10);
    await yieldToUI(20);
    // STEP 2: Load paragraph details in small chunks using getParagraphByUniqueLocalId
    console.log("[getParagraphsForIndexingAsync] Step 2: Loading paragraph details in chunks...");
    const allParagraphsData = [];
    const totalIds = paragraphIds.length;
    for(let chunkStart = 0; chunkStart < totalIds; chunkStart += batchSize){
        const chunkEnd = Math.min(chunkStart + batchSize, totalIds);
        const chunkIds = paragraphIds.slice(chunkStart, chunkEnd);
        try {
            const chunkData = await Word.run(async (context)=>{
                const results = [];
                // Load each paragraph by its unique ID
                const paragraphProxies = chunkIds.map((id)=>{
                    const para = context.document.getParagraphByUniqueLocalId(id);
                    para.load("text, uniqueLocalId, styleBuiltIn, isNullObject");
                    return para;
                });
                await context.sync();
                // Extract data from loaded paragraphs
                for (const para of paragraphProxies){
                    if (!para.isNullObject) {
                        results.push({
                            text: para.text || "",
                            uniqueLocalId: para.uniqueLocalId || "",
                            styleBuiltIn: para.styleBuiltIn || ""
                        });
                    }
                }
                return results;
            });
            allParagraphsData.push(...chunkData);
        } catch (e) {
            console.warn(`[getParagraphsForIndexingAsync] Chunk ${chunkStart}-${chunkEnd} failed:`, e);
        // Continue with next chunk even if this one fails
        }
        // Update progress: 10% to 70% during chunk loading
        const loadProgress = 10 + chunkEnd / totalIds * 60;
        if (onProgress) onProgress(Math.min(loadProgress, 70));
        // Yield to UI between chunks to prevent freezing
        await yieldToUI(10);
        // Log progress every 5 chunks
        if (chunkStart / batchSize % 5 === 0) {
            console.log(`[getParagraphsForIndexingAsync] Loaded ${allParagraphsData.length}/${totalIds} paragraphs`);
        }
    }
    console.log("[getParagraphsForIndexingAsync] Loaded all paragraph data:", allParagraphsData.length);
    if (allParagraphsData.length === 0) {
        if (onProgress) onProgress(100);
        return [];
    }
    if (onProgress) onProgress(75);
    await yieldToUI(20);
    // STEP 3: Process data (pure JS, no Word API calls)
    console.log("[getParagraphsForIndexingAsync] Step 3: Processing paragraph data...");
    const turndownService = new TurndownService({
        headingStyle: "atx"
    });
    addTrackChangesRules(turndownService);
    const thresholdLevel = await getOrSetLevel();
    const paragraphs = [];
    let latestSeenHeaderIndentLevel = null;
    const indentPosition = [];
    const maxIndentPosition = 9;
    const getParagraphPosition = (level)=>{
        const normalizedLevel = Math.max(0, Math.min(level, maxIndentPosition));
        indentPosition.length = normalizedLevel + 1;
        indentPosition[normalizedLevel] = (indentPosition[normalizedLevel] ?? -1) + 1;
        for(let i = 0; i < indentPosition.length; i++){
            if (indentPosition[i] === undefined) {
                indentPosition[i] = 0;
            }
        }
        return [
            ...indentPosition
        ];
    };
    // Process in chunks with yields (pure JS processing)
    const processingChunkSize = 50;
    for(let startIndex = 0; startIndex < allParagraphsData.length; startIndex += processingChunkSize){
        const endIndex = Math.min(startIndex + processingChunkSize, allParagraphsData.length);
        for(let i = startIndex; i < endIndex; i++){
            const para = allParagraphsData[i];
            const level = sectionHeadingLevel(para, thresholdLevel);
            const isHeader = level !== null;
            if (isHeader) {
                latestSeenHeaderIndentLevel = level;
            }
            // Simple markdown - just use the text with header formatting
            const markdown = toMarkdown(stripWordExportNoise(para.text), {
                headerIndent: level ?? undefined
            });
            paragraphs.push({
                position: getParagraphPosition(latestSeenHeaderIndentLevel ?? 0),
                text: markdown,
                hash: para.uniqueLocalId,
                trackedChanges: {
                    total: 0,
                    byType: {},
                    added: [],
                    deleted: []
                }
            });
        }
        // Update progress: 75% to 95% during processing
        const processProgress = 75 + endIndex / allParagraphsData.length * 20;
        if (onProgress) onProgress(Math.min(processProgress, 95));
        await yieldToUI(5);
    }
    const processedSections = paragraphs.filter((section)=>section.text.trim().length > 0);
    if (onProgress) onProgress(100);
    await yieldToUI(10);
    console.log("[getParagraphsForIndexingAsync] Complete! Processed", processedSections.length, "paragraphs");
    return processedSections;
};
